import { useState, useEffect, useMemo, useRef } from 'react';
import { geoOrthographic, geoPath, geoGraticule } from 'd3-geo';
import { feature } from 'topojson-client';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactGA from 'react-ga4';

// --- IMPORT AUTOMATYCZNEGO SŁOWNIKA ---
import countriesRegistry from 'i18n-iso-countries';
import plLocale from 'i18n-iso-countries/langs/pl.json';

countriesRegistry.registerLocale(plLocale);
window.countriesRegistry = countriesRegistry;

const getCountryName = (countryProperties) => {
  const englishName =
    typeof countryProperties === "string"
      ? countryProperties
      : countryProperties?.name;

  const alpha2Code = countriesRegistry.getAlpha2Code(englishName, "en");

  return alpha2Code
    ? countriesRegistry.getName(alpha2Code, "pl") || englishName
    : englishName;
};


export default function Globe() {
  const API_URL = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();

  const [worldData, setWorldData] = useState(null);
  const [rotation, setRotation] = useState([0, 0]);
  const [zoomScale, setZoomScale] = useState(1);

  const [hoveredCountryId, setHoveredCountryId] = useState(null);
  const [selectedCountryId, setSelectedCountryId] = useState(null);

  const [userCountries, setUserCountries] = useState([]);

  // --- NOWY STAN DLA TOOLTIPU ---
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0 });

  const isDragging = useRef(false);
  const prevMousePosition = useRef({ x: 0, y: 0 });
  const dragMoved = useRef(false);

  const width = window.innerWidth;
  const height = window.innerHeight - 80;
  const baseRadius = Math.min(width, height) / 2 - 40;
  const currentRadius = baseRadius * zoomScale;

  const initialPinchDistance = useRef(null);
  const initialScale = useRef(1);

  useEffect(() => {
    fetch('/world-data.json')
      .then((res) => {
        if (!res.ok) throw new Error("Nie znaleziono pliku mapy");
        return res.json();
      })
      .then((data) => setWorldData(data))
      .catch((err) => console.error("Problem z pobieraniem mapy:", err));
  }, []);

  const projection = useMemo(() => {
    return geoOrthographic()
      .scale(currentRadius)
      .translate([width / 2, height / 2])
      .rotate(rotation)
      .clipAngle(89.5)
      .precision(0.1);
  }, [width, height, currentRadius, rotation]);

  const pathGenerator = geoPath().projection(projection);
  const graticulePath = pathGenerator(geoGraticule()());
  const globePath = pathGenerator({ type: "Sphere" });

  const countries = useMemo(() => {
    if (!worldData || !worldData.objects || !worldData.objects.countries) return [];
    try {
      return feature(worldData, worldData.objects.countries).features;
    } catch (e) {
      return [];
    }
  }, [worldData]);

  useEffect(() => {
    const userId = localStorage.getItem("userId");

    if (!userId || countries.length === 0) {
      return;
    }

    axios
      .get(`${API_URL}/trips/visited/${userId}`)
      .then(response => {

        console.log("BACKEND ISO2:", response.data);

        const numericIds = response.data
          .map((isoCode) => {

            const cleanIso2 = String(isoCode)
              .trim()
              .toUpperCase();

            const country = countries.find((country) => {

              const englishName = country.properties?.name;

              if (!englishName) {
                return false;
              }

              const countryNameOverrides = {
                "Dem. Rep. Congo": "CD",
                "S. Sudan": "SS",
                "W. Sahara": "EH",
                "Côte d'Ivoire": "CI",
                "Eq. Guinea": "GQ",
                "Central African Rep.": "CF",
                "Congo": "CG"
              };

              const mapIso2 =
                countryNameOverrides[englishName] ||
                countriesRegistry.getAlpha2Code(englishName, "en");

              return mapIso2 === cleanIso2;
            });

            if (!country) {
              console.warn(
                "Nie znaleziono kraju na mapie dla ISO2:",
                cleanIso2
              );

              return null;
            }

            const englishName = country.properties?.name;
            const numericId = String(Number(country.id));

            console.log(
              cleanIso2,
              "→",
              englishName,
              "→",
              numericId
            );

            return numericId;
          })
          .filter(Boolean);

        console.log(
          "FINAL USER COUNTRIES:",
          numericIds
        );

        setUserCountries(numericIds);

        localStorage.setItem(
          "visitedCountriesNo",
          numericIds.length
        );
      })
      .catch(error => {
        console.error(
          "Błąd pobierania odwiedzonych krajów:",
          error
        );
      });

  }, [API_URL, countries]);

  const handleMouseDown = (e) => {
    isDragging.current = true;
    dragMoved.current = false;
    prevMousePosition.current = { x: e.clientX, y: e.clientY };
    // Ukrywamy tooltip podczas obracania, żeby nie "skakał" niepotrzebnie
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const handleMouseMove = (e) => {
    if (!isDragging.current) return;

    const deltaX = e.clientX - prevMousePosition.current.x;
    const deltaY = e.clientY - prevMousePosition.current.y;

    if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
      dragMoved.current = true;
    }

    const sensitivity = 0.25 / zoomScale;
    setRotation((prev) => [
      prev[0] + deltaX * sensitivity,
      Math.max(-60, Math.min(60, prev[1] - deltaY * sensitivity))
    ]);

    prevMousePosition.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseUp = () => {
    isDragging.current = false;
  };

  const handleWheel = (e) => {
    const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
    setZoomScale((prevScale) => {
      const newScale = prevScale * zoomFactor;
      return Math.max(0.4, Math.min(5, newScale));
    });
    // Ukrywamy tooltip w trakcie scrollowania
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  const handleCountryClick = (countryId, countryPath, countryProperties) => {
    if (dragMoved.current) return;

    setSelectedCountryId((prev) => (prev === countryId ? null : countryId));
    console.log("IDDDDD KRAJUUU: ", countryId)

    const countryName = getCountryName(countryProperties, countryId);

    if (countryName) {
      localStorage.setItem("selectedCountryName", countryName);
    }

    // --- SPRAWDZENIE LOGOWANIA ---
    const userId = localStorage.getItem("userId");

    localStorage.setItem("selectedCountryCode", countriesRegistry.getAlpha2Code(countryName, "en"));

    //GA
    ReactGA.event({
      category: 'Globe',
      action: 'globe_click_country',
      label: countryName
    });

    if (!userId) {
      // Jeśli użytkownik NIE JEST zalogowany -> przekierowanie do strony dla gości
      navigate("/dolacz", {
        state: {
          selectedCountryCode: countryId
        }
      });
    } else {
      // Jeśli użytkownik JEST zalogowany -> przejście do wpisów
      navigate("/wpis", {
        state: {
          id: countryId,
          pathData: countryPath
        }
      });
    }
  };

  // --- LOGIKA OBSŁUGI TOOLTIPU ---
  const handleCountryMouseEnter = (e, countryId, countryProperties) => {
    if (isDragging.current) return; // nie pokazuj dymka, jeśli kręcimy globusem

    const numericCode = String(Number(countryId));
    const countryName = numericCode === "383"
      ? "Kosowo"
      : getCountryName(countryProperties, countryId);

    setTooltip({
      visible: true,
      text: countryName,
      x: e.clientX,
      y: e.clientY
    });
    setHoveredCountryId(countryId);
  };

  const handleCountryMouseMove = (e) => {
    if (isDragging.current || !tooltip.visible) return;

    // Dynamicznie aktualizujemy pozycję dymka w ślad za ruchem myszki
    setTooltip((prev) => ({
      ...prev,
      x: e.clientX,
      y: e.clientY
    }));
  };

  const handleCountryMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
    setHoveredCountryId(null);
  };


  //NA TELEFONY I TABLETY DOTYK
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      isDragging.current = true;
      dragMoved.current = false;
      prevMousePosition.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      setTooltip((prev) => ({ ...prev, visible: false }));
    } else if (e.touches.length === 2) {
      isDragging.current = false;
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      initialPinchDistance.current = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );
      initialScale.current = zoomScale;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 1 && isDragging.current) {
      const touch = e.touches[0];
      const deltaX = touch.clientX - prevMousePosition.current.x;
      const deltaY = touch.clientY - prevMousePosition.current.y;

      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
        dragMoved.current = true;
      }

      const sensitivity = 0.25 / zoomScale;
      setRotation((prev) => [
        prev[0] + deltaX * sensitivity,
        Math.max(-60, Math.min(60, prev[1] - deltaY * sensitivity))
      ]);

      prevMousePosition.current = { x: touch.clientX, y: touch.clientY };
    } else if (e.touches.length === 2 && initialPinchDistance.current) {
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      const dist = Math.hypot(
        touch1.clientX - touch2.clientX,
        touch1.clientY - touch2.clientY
      );

      const newScale = initialScale.current * (dist / initialPinchDistance.current);
      setZoomScale(Math.max(0.4, Math.min(5, newScale)));
    }
  };

  const handleTouchEnd = () => {
    isDragging.current = false;
    initialPinchDistance.current = null;
  };

  if (!worldData) {
    return (
      <div style={{ width, height, backgroundColor: '#ffffff' }}>
        <svg width={width} height={height}>
          <path d={globePath} fill="#F0F4F8" stroke="#e2e8f0" strokeWidth="1" />
        </svg>
      </div>
    );
  }


  return (
    <div
      style={{
        width: "100%",
        height: 'calc(100vh - 80px)',
        position: 'relative',
        cursor: isDragging.current ? 'grabbing' : 'grab',
        userSelect: 'none',
        margin: '0 auto',
        display: 'flex',
        justifyContent: 'center',
        touchAction: 'none',
        alignItems: 'center'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onWheel={handleWheel}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <svg width={window.innerWidth} height={window.innerHeight - 80} style={{ overflow: 'hidden' }}>
        <path d={globePath} fill="#F0F4F8" stroke="#e2e8f0" strokeWidth="1" /> {/*tło kuli ziemskiej*/}
        <path d={graticulePath} fill="none" stroke="#d1d5db" strokeWidth="0.5" strokeOpacity="0.4" />{/*siatka geograficzna*/}
        <g>
          {countries.map((country) => {

            const countryPath = pathGenerator(country);
            if (!countryPath) return null;

            const countryId = String(Number(country.id));

            const isSelected = selectedCountryId === countryId;
            const isHovered = hoveredCountryId === countryId;
            const isVisited = userCountries.includes(countryId);

            let countryColor = "#cbd5e1";
            if (isVisited) {
              countryColor = "#5c85c6";
            } else if (isHovered) {
              countryColor = "#345172";
            } else if (isSelected) {
              countryColor = "#6f9dc5";
            }

            return (
              <path
                key={countryId}
                d={countryPath}
                fill={countryColor}
                stroke="#ffffff"
                strokeWidth="0.8" // Stała krawędź
                style={{
                  transition: 'fill 0.1s ease',
                  cursor: 'pointer',
                  vectorEffect: 'non-scaling-stroke' // Ostre linie przy zbliżeniach
                }}
                onMouseEnter={(e) => handleCountryMouseEnter(e, countryId, country.properties)}
                onMouseMove={handleCountryMouseMove}
                onMouseLeave={handleCountryMouseLeave}
                onClick={() => handleCountryClick(countryId, countryPath, country.properties)}
              />
            );
          })}
        </g>
      </svg>

      {/* --- RENDEROWANIE ELEMENTU TOOLTIP (DYMEK) --- */}
      {tooltip.visible && (
        <div
          style={{
            position: 'fixed', // Pozycjonowanie względem okna ekranu
            left: tooltip.x + 15, // Mały offset (15px w prawo od kursora), żeby dymek nie zasłaniał myszki
            top: tooltip.y - 35, // Wyciągamy dymek lekko nad kursor (35px w górę)
            pointerEvents: 'none', // Sprawia, że dymek jest przezroczysty dla kliknięć (myszka klika przez niego)
            backgroundColor: 'rgba(255, 255, 255, 0.95)', // Białe, delikatnie przezroczyste tło amCharts
            color: '#1e293b',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '500',
            fontFamily: 'sans-serif',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)', // Ładny, miękki cień
            border: '1px solid #e2e8f0',
            zIndex: 9999,
            transition: 'left 0.05s ease, top 0.05s ease' // Gładkie nadążanie za kursorem
          }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
}